/**
 * WITHDRAW (FR-U-WAL-006). Move fiat out to M-Pesa or a bank account, within caps.
 *
 * Four steps, in the order a person asks the questions: WHERE, then HOW MUCH, then CHECK, then DONE.
 * It calls `POST /wallet/withdrawals`, which locks the value through the pipeline BEFORE instructing
 * the rail, so the participant's caps, risk score, whitelist and escrow all apply. That ordering is
 * the requirement, not an implementation detail: instructing first would be paying out money the cap
 * enforcer had not agreed to.
 *
 * ## The crash this screen used to have, and why it is worth a paragraph
 *
 * The balance arrived from the API already formatted ("225,000.00"), and this file parsed it with a
 * private `toMinorUnits` that split on "." and handed the rest to `BigInt`. `BigInt("225,000")`
 * throws. It threw inside a `useMemo`, during render, so it escaped the component entirely and
 * surfaced as React Router's own "Unexpected Application Error" page: a participant pressing Withdraw
 * saw a stack trace and no balance.
 *
 * The comma was the trigger. The DEFECT was that four screens each had their own version of that
 * conversion, so the same string was safe in one and fatal in the next. Parsing now goes through
 * `lib/money.ts` -- one implementation, which strips display formatting before it parses and refuses
 * rather than rounds -- and the route has an error boundary so the next unhandled throw is a sentence
 * instead of a stack trace.
 *
 * ## Three things that are deliberate and easy to "tidy" into being wrong
 *
 * A REFUSAL THROWS. Unlike `/transact/send`, which answers HTTP 200 carrying a refusal body, a
 * blocked withdrawal raises `RampRefused` server side and arrives as a 422. So the refusal is caught,
 * not read off the resolved value, and what gets shown is the DISCLOSABLE reason the backend chose.
 *
 * "INSTRUCTED" IS NOT "PAID". On success the money is locked and gone from the spendable balance, but
 * it has not left the platform. It does that when the rail confirms, and it comes back if the rail
 * refuses. The confirmation copy says exactly that, because a participant told "sent" will go looking
 * for it at the other end.
 *
 * ONLY A VERIFIED DESTINATION IS OFFERED. Connected accounts already state that rule; this screen is
 * where it has to bite. An unverified M-Pesa number is listed, disabled, and labelled, rather than
 * hidden, so somebody who expected to see it knows why it is not selectable. A participant with no
 * verified destination on a rail is sent to add one -- never given a free-text field, which would
 * route a payout to a number nobody has checked.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  api,
  LipaApiError,
  type PayoutDestination,
  type WithdrawalRail,
  type WithdrawalReceipt,
} from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';
import {
  formatKenyanMsisdn,
  formatMoneyAmount,
  fromBaseUnits,
  normalizeKenyanMsisdn,
  parseMoneyAmount,
  sanitizeAmountTyping,
} from '@/lib/money';
import { newIdempotencyKey } from '@/lib/ids';
import { useResource } from '@/lib/use-resource';
import { useSession } from '@/lib/session-context';

type Step = 'method' | 'details' | 'review' | 'done';

/**
 * The fee, today, is nothing, and this is the ONE place that says so.
 *
 * There is no withdrawal fee schedule in the platform: `POST /wallet/withdrawals` charges none and
 * returns none. Rendering an invented figure would be inventing a price, which on a conduct-regulated
 * product is not a UI detail. When a schedule exists it is served with the quote and this constant
 * goes, along with the note beside it.
 */
const WITHDRAWAL_FEE_MINOR = 0n;

export default function Withdraw() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { me } = useSession();
  const participantId = me?.participant_id ?? null;

  const [step, setStep] = useState<Step>('method');
  const [rail, setRail] = useState<WithdrawalRail | null>(null);

  // Prefilled when arriving from Earnings, which passes what has settled and is withdrawable. It
  // arrives formatted, and `sanitizeAmountTyping` is what the field holds, so the comma comes off
  // here rather than blowing up in a parser three functions away.
  const [amount, setAmount] = useState(() => sanitizeAmountTyping(params.get('amount') ?? ''));
  const fromEarnings = params.get('source') === 'earnings';

  const [destinationId, setDestinationId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [refusal, setRefusal] = useState('');
  const [touched, setTouched] = useState(false);
  const [receipt, setReceipt] = useState<WithdrawalReceipt | null>(null);

  /**
   * A key identifies ONE INTENT: this amount, to this destination. Not one screen, and not one
   * attempt.
   *
   * Both other readings are wrong, in opposite directions, and the backend rejects each. A fresh key
   * per attempt makes a retry a SECOND payout. A single key per screen is what this file did first,
   * and it dead-ends the most ordinary path there is: the cap refuses KES 95,000, the participant
   * lowers it to KES 3,000, and the request comes back `IDEMPOTENCY_KEY_REUSED` because the key was
   * already spent on a different amount. Nothing they can type will work until they reload the page.
   *
   * Seeding from the mount and folding in the intent gets both: press again with the same amount and
   * destination and it is the same key, so it is the same withdrawal; change either and it is a new
   * one, because it is a new request.
   */
  const [keySeed] = useState(() => newIdempotencyKey());

  const wallet = useResource(
    () => (participantId ? api.wallet(participantId) : Promise.resolve(null)),
    [participantId],
  );
  const accounts = useResource(() => api.connectedAccounts(), []);

  const destinations: PayoutDestination[] = useMemo(() => {
    if (!accounts.data) return [];
    return [
      ...accounts.data.mpesa.map((m) => ({
        id: `mpesa:${m.id}`,
        rail: 'mobile_money' as const,
        label: 'M-Pesa',
        masked: formatKenyanMsisdn(m.number),
        ref: m.number,
        verified: m.verified,
      })),
      ...accounts.data.banks.map((b) => ({
        id: `bank:${b.id}`,
        rail: 'bank_transfer' as const,
        label: b.name,
        masked: b.masked,
        ref: b.masked,
        // A bank account reaches this list only once it is on file, and being on file is the
        // verification. M-Pesa carries its own flag because a number can be added unverified.
        verified: true,
      })),
    ];
  }, [accounts.data]);

  const onRail = useMemo(() => destinations.filter((d) => d.rail === rail), [destinations, rail]);

  // Preselect the only usable destination on the chosen rail. One radio button that the participant
  // must still click is a step that exists to be got through.
  useEffect(() => {
    if (!rail) return;
    const usable = onRail.filter((d) => d.verified);
    setDestinationId((current) =>
      usable.some((d) => d.id === current) ? current : (usable[0]?.id ?? ''),
    );
  }, [rail, onRail]);

  /**
   * The available balance, parsed from what the API sent.
   *
   * `parseMoneyAmount` RETURNS a result rather than throwing, which is the whole point: this runs on
   * every render, and a throw here is what produced the error page. A balance we cannot parse is
   * treated as zero and said out loud, rather than silently becoming an amount.
   */
  const availableParsed = useMemo(() => {
    const kes = wallet.data?.balances?.find((b) => b.asset === 'KES');
    return kes ? parseMoneyAmount(kes.available) : ({ ok: true, minor: 0n } as const);
  }, [wallet.data]);

  const available = availableParsed.ok ? availableParsed.minor : 0n;
  const parsed = parseMoneyAmount(amount);
  const requested = parsed.ok ? parsed.minor : 0n;
  const chosen = destinations.find((d) => d.id === destinationId);

  /** The one place that decides whether this form may be submitted, and why not. */
  const amountError: string | null = (() => {
    if (!amount.trim()) return 'Enter a valid withdrawal amount.';
    if (!parsed.ok) return parsed.reason;
    if (parsed.minor <= 0n) return 'Enter a withdrawal amount above zero.';
    if (parsed.minor > available) {
      return 'You do not have enough balance to complete this withdrawal.';
    }
    return null;
  })();

  const destinationError: string | null = (() => {
    if (!chosen) {
      return rail === 'mobile_money'
        ? 'Choose a verified M-Pesa number to withdraw to.'
        : 'Choose a verified bank account to withdraw to.';
    }
    if (chosen.rail === 'mobile_money' && !normalizeKenyanMsisdn(chosen.ref)) {
      return 'Enter a valid M-Pesa phone number.';
    }
    return null;
  })();

  const youReceive = requested > WITHDRAWAL_FEE_MINOR ? requested - WITHDRAWAL_FEE_MINOR : 0n;
  const idempotencyKey = `${keySeed}-${requested}-${destinationId}`;

  if (wallet.loading || accounts.loading)
    return (
      <div className="mx-auto max-w-md space-y-5">
        <LoadingCard lines={2} />
        <LoadingCard lines={4} />
      </div>
    );

  if (wallet.error || accounts.error)
    return (
      <ErrorState
        body="We could not load your balance or your accounts."
        onRetry={() => {
          wallet.reload();
          accounts.reload();
        }}
      />
    );

  async function submit() {
    if (!chosen || amountError || destinationError) return;
    setSubmitting(true);
    setRefusal('');
    try {
      // The exact decimal string, normalised. What the participant typed with a stray comma or a
      // trailing space is not what goes on the wire.
      const exact = parsed.ok ? parsed.normalized : amount;
      setReceipt(await api.withdraw(exact, 'KES', chosen.rail, chosen.ref, idempotencyKey));
      setStep('done');
    } catch (err) {
      // The disclosable reason, chosen by the backend. Never the internal one, and never a guess:
      // "your single transaction limit" and "we cannot process this right now" are different
      // sentences to a participant and only one of them is ours to invent.
      setRefusal(
        err instanceof LipaApiError
          ? err.detail.message
          : 'We could not process your withdrawal right now. Please try again. Nothing has left your balance.',
      );
      setStep('details');
    } finally {
      setSubmitting(false);
    }
  }

  if (step === 'done' && receipt) {
    return (
      <div className="mx-auto max-w-md py-12 text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent/15 text-accent">
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m5 13 4 4L19 7" />
          </svg>
        </span>
        <h1 className="mt-4 font-semibold text-ink">Withdrawal submitted</h1>
        <p className="mx-auto mt-1 max-w-sm text-sm text-ink-muted">
          KES {receipt.amount} to your {chosen?.label ?? 'account'}.
        </p>

        <dl className="mx-auto mt-5 max-w-sm space-y-2 rounded-card border border-border bg-surface p-4 text-left text-sm">
          <SummaryRow label="Reference" value={receipt.instruction_id} mono />
          {/* The backend's own state word, not a friendlier one of ours. "Pending" and "instructed"
              are the same fact, and support will be looking at the second. */}
          <SummaryRow label="Status" value={statusLabel(receipt.state)} />
        </dl>

        {/* The backend's own sentence, verbatim. It is the one that draws the line between held and
            paid, and a paraphrase is exactly where that meaning would go. */}
        <p className="mx-auto mt-3 max-w-sm rounded-card bg-surface-sunken p-3 text-xs text-ink-muted">
          {receipt.note}
        </p>

        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <Link
            to="/wallet"
            className="inline-flex rounded-field bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-soft transition hover:bg-primary-light"
          >
            Back to wallet
          </Link>
          <button
            onClick={() => navigate(`/activity/${receipt.correlation_id}`)}
            className="inline-flex rounded-field border border-border px-4 py-2.5 text-sm font-medium text-ink transition hover:bg-surface-sunken"
          >
            Track it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-5">
      <button
        onClick={() => {
          if (step === 'review') return setStep('details');
          if (step === 'details') return setStep('method');
          navigate(fromEarnings ? '/earnings' : '/wallet');
        }}
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
        </svg>
        {step === 'method' ? (fromEarnings ? 'Earnings' : 'Wallet') : 'Back'}
      </button>

      <header>
        <h1 className="text-page-title text-ink">
          {step === 'method'
            ? 'Where do you want to withdraw?'
            : step === 'review'
              ? 'Confirm withdrawal'
              : rail === 'mobile_money'
                ? 'Withdraw to M-Pesa'
                : 'Withdraw to bank'}
        </h1>
        <p className="mt-0.5 text-sm text-ink-muted">
          Available: KES {formatMoneyAmount(available)}
          {fromEarnings ? ' · prefilled from your earnings' : ''}
        </p>
        {!availableParsed.ok ? (
          <p className="mt-1 text-xs text-warning">
            We could not read your balance, so this screen is showing zero. Nothing is wrong with
            your money. Try again in a moment.
          </p>
        ) : null}
      </header>

      {/* STEP 1. WHERE. */}
      {step === 'method' ? (
        <div className="space-y-3">
          <MethodCard
            title="M-Pesa"
            body="Withdraw your funds to an M-Pesa mobile number."
            available={destinations.some((d) => d.rail === 'mobile_money' && d.verified)}
            onSelect={() => {
              setRail('mobile_money');
              setStep('details');
            }}
            icon={
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M8 3h8a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm3 15h2"
              />
            }
          />
          <MethodCard
            title="Bank"
            body="Withdraw your funds to a bank account."
            available={destinations.some((d) => d.rail === 'bank_transfer' && d.verified)}
            onSelect={() => {
              setRail('bank_transfer');
              setStep('details');
            }}
            icon={
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M3 10 12 4l9 6M5 10v9M19 10v9M9 19v-5h6v5M3 20h18"
              />
            }
          />
          <p className="rounded-card bg-surface-sunken p-3 text-xs text-ink-muted">
            Both rails pay out only to a destination you have already verified.{' '}
            <Link to="/settings/accounts" className="font-medium text-brand underline">
              Manage your accounts
            </Link>
            .
          </p>
        </div>
      ) : null}

      {/* STEP 2. HOW MUCH, AND TO WHICH ONE. */}
      {step === 'details' ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setTouched(true);
            if (!amountError && !destinationError) setStep('review');
          }}
          className="space-y-5 rounded-xl border border-border bg-surface p-6 shadow-card"
        >
          <div>
            <label className="block text-sm font-medium text-ink">
              {rail === 'mobile_money' ? 'M-Pesa number' : 'Bank account'}
            </label>

            {onRail.length === 0 ? (
              <p className="mt-2 rounded-card bg-surface-sunken p-3 text-xs text-ink-muted">
                You have no verified {rail === 'mobile_money' ? 'M-Pesa number' : 'bank account'}{' '}
                yet.{' '}
                <Link to="/settings/accounts" className="font-medium text-brand underline">
                  Add one
                </Link>
                . We only pay out to a destination that has been verified, so this cannot be typed
                in here.
              </p>
            ) : (
              <div className="mt-2 space-y-2">
                {onRail.map((d) => (
                  <label
                    key={d.id}
                    className={`flex items-center justify-between gap-3 rounded-field border px-3 py-3 text-sm transition ${
                      !d.verified
                        ? 'cursor-not-allowed border-border opacity-60'
                        : destinationId === d.id
                          ? 'cursor-pointer border-primary bg-primary-tint'
                          : 'cursor-pointer border-border hover:bg-surface-sunken'
                    }`}
                  >
                    <span className="min-w-0">
                      <span
                        className={`block font-medium ${destinationId === d.id ? 'text-brand' : 'text-ink'}`}
                      >
                        {d.label}
                      </span>
                      <span className="block truncate text-xs text-ink-muted">
                        {d.masked}
                        {d.verified ? '' : ' · not verified yet'}
                      </span>
                    </span>
                    <input
                      type="radio"
                      name="dest"
                      value={d.id}
                      disabled={!d.verified}
                      checked={destinationId === d.id}
                      onChange={() => setDestinationId(d.id)}
                      className="shrink-0 accent-primary"
                    />
                  </label>
                ))}
              </div>
            )}

            {touched && destinationError ? (
              <p className="mt-1 text-xs text-danger">{destinationError}</p>
            ) : null}
          </div>

          <div>
            <label htmlFor="amt" className="block text-sm font-medium text-ink">
              Amount
            </label>
            <div className="mt-1 flex items-center gap-2 rounded-field border border-border px-3 py-2.5 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/25">
              <span className="text-sm text-ink-muted">KES</span>
              <input
                id="amt"
                autoFocus
                inputMode="decimal"
                value={amount}
                aria-invalid={touched && !!amountError}
                onChange={(e) => setAmount(sanitizeAmountTyping(e.target.value))}
                onBlur={() => setTouched(true)}
                placeholder="0.00"
                className="w-full min-w-0 bg-transparent font-mono text-base text-ink outline-none"
              />
              <button
                type="button"
                // The whole balance, as an EXACT decimal string. `fromBaseUnits` is the round-trip
                // form and `formatMoneyAmount` is the reading form; putting the grouped one into a
                // field and parsing it back is the exact round trip this screen used to crash on.
                onClick={() => setAmount(fromBaseUnits(available))}
                className="shrink-0 rounded-pill border border-border px-2 py-0.5 text-xs text-ink-muted transition hover:bg-surface-sunken"
              >
                All
              </button>
            </div>
            {touched && amountError ? (
              <p className="mt-1 text-xs text-danger">{amountError}</p>
            ) : (
              <p className="mt-1 text-xs text-ink-subtle">
                Your daily and per transaction limits still apply.
              </p>
            )}
          </div>

          <dl className="space-y-2 rounded-card bg-surface-sunken p-3 text-sm">
            <SummaryRow label="Available balance" value={`KES ${formatMoneyAmount(available)}`} />
            <SummaryRow label="Fee" value={`KES ${formatMoneyAmount(WITHDRAWAL_FEE_MINOR)}`} />
            <SummaryRow label="You receive" value={`KES ${formatMoneyAmount(youReceive)}`} strong />
          </dl>

          {refusal ? (
            <p
              role="alert"
              className="rounded-card border border-danger/30 bg-danger/10 p-3 text-sm text-danger"
            >
              {refusal}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={!!amountError || !!destinationError}
            className="w-full rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
          >
            Review withdrawal
          </button>
        </form>
      ) : null}

      {/* STEP 3. CHECK. Nothing new is entered here; it is the last look before value moves. */}
      {step === 'review' && chosen ? (
        <div className="space-y-5 rounded-xl border border-border bg-surface p-6 shadow-card">
          <dl className="space-y-2 text-sm">
            <SummaryRow
              label="Withdrawal method"
              value={rail === 'mobile_money' ? 'M-Pesa' : 'Bank transfer'}
            />
            <SummaryRow label="Destination" value={chosen.masked} mono />
            <SummaryRow label="Amount" value={`KES ${formatMoneyAmount(requested)}`} />
            <SummaryRow label="Fee" value={`KES ${formatMoneyAmount(WITHDRAWAL_FEE_MINOR)}`} />
            <SummaryRow label="You receive" value={`KES ${formatMoneyAmount(youReceive)}`} strong />
          </dl>

          {/* What actually happens next, which is not "the money arrives". */}
          <div className="rounded-card bg-surface-sunken p-3 text-xs leading-relaxed text-ink-muted">
            <p className="font-medium text-ink">Your money is held in escrow first.</p>
            <p className="mt-1">
              It leaves your available balance as soon as you confirm, and it is held while the
              checks run and the payout is instructed. It reaches your{' '}
              {rail === 'mobile_money' ? 'M-Pesa wallet' : 'bank account'} when the rail confirms,
              and it comes back to you if the rail refuses.
            </p>
            <p className="mt-1">
              We charge no fee for this. Your bank or mobile money operator may charge their own,
              and that is not something we can see.
            </p>
          </div>

          {refusal ? (
            <p
              role="alert"
              className="rounded-card border border-danger/30 bg-danger/10 p-3 text-sm text-danger"
            >
              {refusal}
            </p>
          ) : null}

          <div className="flex gap-3">
            <button
              onClick={() => setStep('details')}
              disabled={submitting}
              className="flex-1 rounded-field border border-border py-3 text-sm font-medium text-ink transition hover:bg-surface-sunken disabled:opacity-50"
            >
              Back
            </button>
            <button
              onClick={submit}
              // Disabled WHILE SUBMITTING, not just styled as such. The idempotency key makes a
              // double submit safe on the backend; this makes it not happen in the first place.
              disabled={submitting || !!amountError || !!destinationError}
              className="flex-1 rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Confirm withdrawal'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** The backend's ramp states, in the participant's words. Unknown states pass through unchanged. */
function statusLabel(state: string): string {
  if (state === 'instructed' || state === 'pending') return 'Pending';
  if (state === 'settled') return 'Paid';
  if (state === 'failed') return 'Failed, and returned to your balance';
  return state;
}

function MethodCard({
  title,
  body,
  available,
  onSelect,
  icon,
}: {
  title: string;
  body: string;
  available: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
}) {
  return (
    <button
      onClick={onSelect}
      className="flex w-full items-center gap-4 rounded-card border border-border bg-surface p-4 text-left shadow-card transition hover:border-primary/40 hover:bg-surface-sunken"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-brand">
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          {icon}
        </svg>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-ink">{title}</span>
        <span className="mt-0.5 block text-sm text-ink-muted">{body}</span>
        {/* Shown, not hidden. A rail with nothing verified behind it is still the right answer to
            "where do you want to withdraw"; it just needs one more step first. */}
        {!available ? (
          <span className="mt-1 block text-xs text-warning">
            Needs a verified destination first
          </span>
        ) : null}
      </span>
      <span aria-hidden className="shrink-0 text-ink-subtle">
        &rarr;
      </span>
    </button>
  );
}

function SummaryRow({
  label,
  value,
  mono,
  strong,
}: {
  label: string;
  value: string;
  mono?: boolean;
  strong?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd
        className={`min-w-0 truncate text-right ${mono ? 'font-mono text-xs' : 'tabular'} ${
          strong ? 'font-semibold text-ink' : 'font-medium text-ink'
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
